import { shouldEncrypt } from "./mediaEncryption";
import type { DeviceFamily } from "./platformCompat";

export type MediaPushMode = "adb" | "agent";

export type MediaPushTarget = DeviceFamily | "desktop";

export interface MediaAdditionalFile {
  name: string;
  s3Url: string;
  sizeInBytes: number;
}

export interface MediaManifestItem {
  id: number;
  type: string;
  s3Url: string | null;
  s3UrlPreset2: string | null;
  s3UrlPreset3: string | null;
  s3UrlPreset4: string | null;
  s3UrlImagePreset5: string | null;
  s3UrlImagePreset6: string | null;
  updatedAt: string | null;
  sizeInBytes: number;
  sizeInBytesPreset2: number | null;
  sizeInBytesPreset3: number | null;
  sizeInBytesPreset4: number | null;
  sizeInBytesImagePreset5: number | null;
  sizeInBytesImagePreset6: number | null;
  name: string | null;
  additionalFiles: MediaAdditionalFile[];
}

export interface MediaFileToPush {
  url: string;
  filename: string;
  sizeInBytes: number;
  encrypt: boolean;
}

export function extractFilename(url: string): string {
  const withoutQuery = url.split("?")[0];
  return withoutQuery.split("/").pop() || "unknown";
}

function presetFile(
  media: MediaManifestItem,
  url: string | null,
  sizeInBytes: number | null,
): MediaFileToPush | null {
  if (!url) return null;
  const filename = extractFilename(url);
  return {
    url,
    filename,
    sizeInBytes: sizeInBytes || 0,
    encrypt: shouldEncrypt(media.type, filename),
  };
}

function platformPresets(
  media: MediaManifestItem,
  target: MediaPushTarget,
): (MediaFileToPush | null)[] {
  const video2 = () => presetFile(media, media.s3UrlPreset2, media.sizeInBytesPreset2);
  const video3 = () => presetFile(media, media.s3UrlPreset3, media.sizeInBytesPreset3);
  const video4 = () => presetFile(media, media.s3UrlPreset4, media.sizeInBytesPreset4);
  const image5 = () => presetFile(media, media.s3UrlImagePreset5, media.sizeInBytesImagePreset5);
  const image6 = () => presetFile(media, media.s3UrlImagePreset6, media.sizeInBytesImagePreset6);
  if (media.type === "video") {
    switch (target) {
      case "desktop":
        return [video4()];
      case "pico":
        return [video2(), video3()];
      default:
        return [video2()];
    }
  }
  if (media.type === "image") {
    switch (target) {
      case "desktop":
        return [];
      case "android":
        return [image5()];
      default:
        return [image5(), image6()];
    }
  }
  return [];
}

export function getMediaFilesToPush(
  media: MediaManifestItem,
  target: MediaPushTarget,
): MediaFileToPush[] {
  const presets = platformPresets(media, target);
  const files = presets.filter((f): f is MediaFileToPush => f !== null);
  if (files.length === 0) {
    const original = presetFile(media, media.s3Url, media.sizeInBytes);
    if (original) files.push(original);
  }
  for (const af of media.additionalFiles ?? []) {
    if (!af.s3Url || !af.name) continue;
    files.push({
      url: af.s3Url,
      filename: af.name,
      sizeInBytes: af.sizeInBytes || 0,
      encrypt: false,
    });
  }
  return files;
}

export function getMediaPushSize(media: MediaManifestItem, target: MediaPushTarget): number {
  return getMediaFilesToPush(media, target).reduce((sum, f) => sum + f.sizeInBytes, 0);
}
